import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { setResolvedOrgId } from '../lib/apiClient';
import {
  apiBaseUrl,
  getDevOrgOverride,
} from '../lib/orgRuntime';

interface OrgMembership {
  membershipId: string;
  orgId: string;
  orgName: string;
  role: string;
  orgWideRead: boolean;
  createdAt: string | null;
  subscriptionStatus: string | null;
  pilotExpiresAt: string | null;
  hasSubscription: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  orgId: string | null;
  orgRole: string | null;
  memberships: OrgMembership[];
  enrollmentError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, inviteCode?: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Persist invite code in sessionStorage so it survives the sign-up -> verify -> sign-in flow
const INVITE_CODE_KEY = 'scopeshield_invite_code';

export function storeInviteCode(code: string) {
  sessionStorage.setItem(INVITE_CODE_KEY, code);
}

function consumeInviteCode(): string | null {
  const code = sessionStorage.getItem(INVITE_CODE_KEY);
  if (code) sessionStorage.removeItem(INVITE_CODE_KEY);
  return code;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState<OrgMembership[]>([]);
  const [orgRole, setOrgRole] = useState<string | null>(null);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
  const [enrollmentDone, setEnrollmentDone] = useState(false);
  const enrolledRef = useRef(false);
  const enrollmentPromiseRef = useRef<Promise<void> | null>(null);

  const devOverrideOrgId = getDevOrgOverride() ?? null;

  async function enrollSession(currentSession: Session): Promise<void> {
    if (enrolledRef.current) return;
    if (enrollmentPromiseRef.current) {
      return enrollmentPromiseRef.current;
    }

    enrollmentPromiseRef.current = (async () => {
      const body: Record<string, string> = {};
      const inviteCode = consumeInviteCode();

      if (inviteCode) {
        body.inviteCode = inviteCode;
      }

      if (devOverrideOrgId) {
        body.orgId = devOverrideOrgId;
      }

      const response = await fetch(`${apiBaseUrl}/auth/enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentSession.access_token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Enrollment failed' }));
        throw new Error(errorData.error || `Enrollment failed with status ${response.status}`);
      }

      const data = await response.json();
      if (!data.ok) {
        throw new Error(data.error || 'Enrollment failed');
      }

      enrolledRef.current = true;
      setEnrollmentError(null);
      setEnrollmentDone(true);
    })();

    try {
      await enrollmentPromiseRef.current;
    } finally {
      enrollmentPromiseRef.current = null;
    }
  }

  async function fetchMemberships(currentSession: Session): Promise<OrgMembership[]> {
    const response = await fetch(`${apiBaseUrl}/auth/memberships`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${currentSession.access_token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to load memberships' }));
      throw new Error(errorData.error || `Failed to load memberships (${response.status})`);
    }

    const data: { ok: boolean; memberships?: OrgMembership[]; error?: string } = await response.json();
    if (!data.ok) {
      throw new Error(data.error || 'Failed to load memberships');
    }

    return data.memberships ?? [];
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session || enrolledRef.current) return;
    if (!apiBaseUrl) return;

    (async () => {
      try {
        await enrollSession(session);
      } catch (err: any) {
        console.error('Auto-enrollment failed:', err);
        setEnrollmentError(err?.message || 'Failed to enroll in organization');
        enrolledRef.current = false;
      }
    })();
  }, [session, devOverrideOrgId]);

  useEffect(() => {
    if (!session) {
      setMemberships([]);
      setOrgRole(null);
      setResolvedOrgId(null);
      return;
    }
    if (!apiBaseUrl) {
      setMemberships([]);
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        let nextMemberships = await fetchMemberships(session);

        if (nextMemberships.length === 0) {
          await enrollSession(session);
          nextMemberships = await fetchMemberships(session);
        }

        if (!isMounted) return;
        setMemberships(nextMemberships);
        setEnrollmentError(null);
      } catch (err: any) {
        if (!isMounted) return;
        console.error('Membership fetch failed:', err);
        setEnrollmentError(err?.message || 'Failed to load memberships');
        setMemberships([]);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [session, enrollmentDone]);

  const activeMembership = useMemo(
    () =>
      memberships.find((membership) => {
        if (membership.subscriptionStatus !== 'active') return false;
        if (!membership.pilotExpiresAt) return true;
        return new Date(membership.pilotExpiresAt) > new Date();
      }) ?? null,
    [memberships],
  );

  const derivedOrgId = useMemo(
    () => devOverrideOrgId ?? activeMembership?.orgId ?? memberships[0]?.orgId ?? null,
    [activeMembership, memberships, devOverrideOrgId],
  );

  useEffect(() => {
    setResolvedOrgId(derivedOrgId);
    const role = memberships.find((m) => m.orgId === derivedOrgId)?.role ?? null;
    setOrgRole(role);
  }, [derivedOrgId, memberships]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string, inviteCode?: string) => {
    if (inviteCode) {
      storeInviteCode(inviteCode);
    }

    const { error } = await supabase.auth.signUp({ email, password });
    return { error };
  };

  const signOut = async () => {
    enrolledRef.current = false;
    enrollmentPromiseRef.current = null;
    setEnrollmentDone(false);
    setMemberships([]);
    setOrgRole(null);
    setResolvedOrgId(null);
    await supabase.auth.signOut();
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    orgId: derivedOrgId,
    orgRole,
    memberships,
    enrollmentError,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
